import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { CalendarPlus, HardHat, Plus, MagnifyingGlass, Check, UsersThree, Airplane } from "@phosphor-icons/react";
import InterventionTypeToggle from "@/components/InterventionTypeToggle";
import InterventionCategoryPicker from "@/components/InterventionCategoryPicker";
import TimeRangeInput from "@/components/TimeRangeInput";
import AssigneesPicker from "@/components/AssigneesPicker";
import ColorPickerField from "@/components/ColorPickerField";
import { useAuth } from "@/context/AuthContext";

const LEAVE_TYPE_OPTS = [
  { v: "paid", label: "Congé payé" },
  { v: "rtt", label: "RTT" },
  { v: "sick", label: "Maladie" },
  { v: "unpaid", label: "Sans solde" },
];

/**
 * Empty-cell quick planner. Two modes depending on the InterventionTypeToggle:
 *  - intervention / dépannage → POST /interventions (chantier + tech + hours)
 *  - congé                    → POST /leave-requests (leave type + date range + reason)
 *                                Admin creating it on a tech's row files it on the tech's
 *                                behalf and auto-approves; a tech creating from their own
 *                                row files a pending request the admin will validate.
 *
 * Chantier field is a live autocomplete (only shown in intervention/dépannage modes).
 */
export default function QuickInterventionDialog({ open, onOpenChange, technicianId, technicianName, dateIso, time, onCreated }) {
  const { user, isAdmin } = useAuth();
  const [chantiers, setChantiers] = useState([]);
  const [team, setTeam] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [chantierQuery, setChantierQuery] = useState("");
  const [chantierListOpen, setChantierListOpen] = useState(false);
  const [creatingChantier, setCreatingChantier] = useState(false);

  const isLeave = form.type === "leave";
  const filingOnBehalf = isAdmin && technicianId && technicianId !== user?.id;

  useEffect(() => {
    if (!open) { setForm({}); setChantierQuery(""); return; }
    const startTime = time || "09:00";
    const initialDate = dateIso ? `${dateIso}T${startTime}` : "";
    setForm({
      technician_id: technicianId,
      assignee_ids: [],
      scheduled_date: initialDate,
      duration_min: 60,
      title: "",
      type: "intervention",
      categorie: "electricite",
      // Leave mode fields (start/end pre-filled to the clicked day)
      leave_type: "paid",
      leave_start: dateIso || "",
      leave_end: dateIso || "",
      leave_reason: "",
    });
    (async () => {
      try {
        const [r, u] = await Promise.all([api.get("/chantiers"), api.get("/users")]);
        setChantiers(r.data);
        setTeam((u.data || []).filter((usr) => usr.is_active !== false));
      } catch (err) { console.warn("fetch failed:", err); }
    })();
  }, [open, technicianId, dateIso, time]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const q = chantierQuery.trim().toLowerCase();
  const matches = q
    ? chantiers.filter((c) =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.address || "").toLowerCase().includes(q))
    : chantiers.slice(0, 12);
  const exactMatch = q && chantiers.some((c) => (c.name || "").toLowerCase() === q);
  const showCreate = q.length >= 2 && !exactMatch;

  const pickChantier = (c) => {
    set("chantier_id", c.id);
    setChantierQuery(c.name);
    setChantierListOpen(false);
    if (!form.title) set("title", "Intervention");
  };

  const createChantier = async () => {
    const name = chantierQuery.trim();
    if (!name) return;
    setCreatingChantier(true);
    try {
      let clientId = null;
      let addressExtra = {};
      try {
        const p = await api.get("/places/search", { params: { q: name, limit: 1 } });
        const hit = (p.data || [])[0];
        if (hit && hit.address) {
          const clientResp = await api.post("/clients", {
            name: hit.name || name,
            address: hit.address,
            postal_code: hit.postal_code || null,
            city: hit.city || null,
            latitude: hit.latitude || null,
            longitude: hit.longitude || null,
          });
          clientId = clientResp.data?.id || null;
          addressExtra = { address: hit.address };
          toast.info(`Client "${clientResp.data?.name}" créé automatiquement`);
        }
      } catch (err) {
        console.debug("[quick-iv] photon lookup failed:", err?.message || err);
      }
      const r = await api.post("/chantiers", {
        name,
        status: "waiting",
        progress: 0,
        client_id: clientId,
        ...addressExtra,
      });
      const created = r.data;
      setChantiers((s) => [created, ...s]);
      pickChantier(created);
      toast.success(clientId ? `Chantier "${created.name}" créé + client rattaché` : `Chantier "${created.name}" créé`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreatingChantier(false);
    }
  };

  const leaveDays = (() => {
    if (!form.leave_start || !form.leave_end || form.leave_end < form.leave_start) return 0;
    const s = new Date(form.leave_start), e = new Date(form.leave_end);
    return Math.max(1, Math.round((e - s) / 86400000) + 1);
  })();

  const submitIntervention = async () => {
    const payload = {
      technician_id: form.technician_id,
      assignee_ids: form.assignee_ids || [],
      scheduled_date: form.scheduled_date,
      duration_min: form.duration_min,
      title: form.title,
      type: form.type,
      categorie: form.categorie || "electricite",
      chantier_id: form.chantier_id,
      color: form.color,
    };
    await api.post("/interventions", payload);
    toast.success(form.type === "depannage" ? "Dépannage planifié" : "Intervention planifiée");
  };

  const submitLeave = async () => {
    const payload = {
      type: form.leave_type,
      start_date: form.leave_start,
      end_date: form.leave_end,
      reason: (form.leave_reason || "").trim() || null,
    };
    if (filingOnBehalf) payload.user_id = technicianId;
    await api.post("/leave-requests", payload);
    toast.success(filingOnBehalf ? `Congé enregistré pour ${technicianName || "l'équipier"}` : "Demande envoyée");
  };

  const submit = async () => {
    // Client-side gate first — must block before optimistic close.
    if (isLeave) {
      if (!form.leave_start || !form.leave_end) { toast.error("Dates de congé requises"); return; }
      if (form.leave_end < form.leave_start) { toast.error("La date de fin doit être postérieure à la date de début"); return; }
    } else if (!form.title || !form.chantier_id || !form.technician_id || !form.scheduled_date) {
      toast.error("Titre, chantier, technicien et date requis");
      return;
    }
    // Optimistic close so the click feels instantaneous. If the API call
    // fails we surface a toast and the user can retrigger via the empty cell.
    setSaving(true);
    onOpenChange(false);
    try {
      if (isLeave) await submitLeave();
      else await submitIntervention();
      onCreated?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#111] border-white/10 text-white rounded-2xl" data-testid="quick-iv-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            {isLeave
              ? <><Airplane size={20} className="text-emerald-400" weight="duotone" /> Nouveau congé</>
              : <><CalendarPlus size={20} className="text-[#005CFF]" weight="duotone" /> Nouvelle intervention</>}
          </DialogTitle>
          <DialogDescription className="text-[11px] uppercase tracking-widest text-white/50 mt-1">
            {technicianName ? `${technicianName} · ` : ""}
            {dateIso ? new Date(dateIso).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }) : "Planifier"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <InterventionTypeToggle value={form.type} onChange={(v) => set("type", v)} testid="quick-iv-type" withLeave />

          {!isLeave && (
            <div className="rounded-xl bg-white/[0.02] border border-white/10 p-2 space-y-1.5">
              <div className="text-[9px] uppercase tracking-widest text-white/50 px-0.5">
                Corps de métier
              </div>
              <InterventionCategoryPicker
                value={form.categorie}
                onChange={(v) => set("categorie", v)}
                testid="quick-iv-categorie"
              />
            </div>
          )}

          {isLeave ? (
            <>
              <Field label="Type de congé *">
                <Select value={form.leave_type} onValueChange={(v) => set("leave_type", v)}>
                  <SelectTrigger className="bg-white/5 border-white/10" data-testid="quick-leave-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111] border-white/10 text-white">
                    {LEAVE_TYPE_OPTS.map((o) => (
                      <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Du *">
                  <Input
                    type="date"
                    value={form.leave_start || ""}
                    onChange={(e) => set("leave_start", e.target.value)}
                    className="bg-white/5 border-white/10 [color-scheme:dark]"
                    data-testid="quick-leave-start"
                  />
                </Field>
                <Field label="Au *">
                  <Input
                    type="date"
                    value={form.leave_end || ""}
                    onChange={(e) => set("leave_end", e.target.value)}
                    className="bg-white/5 border-white/10 [color-scheme:dark]"
                    data-testid="quick-leave-end"
                  />
                </Field>
              </div>

              {leaveDays > 0 && (
                <div className="text-[10px] text-white/50 text-center">
                  Soit <span className="font-mono text-emerald-300">{leaveDays} jour{leaveDays > 1 ? "s" : ""}</span>
                  {filingOnBehalf && <> — sera <b className="text-emerald-300">automatiquement validé</b> car créé par le gérant</>}
                </div>
              )}

              <Field label="Motif (optionnel)">
                <Textarea
                  value={form.leave_reason || ""}
                  onChange={(e) => set("leave_reason", e.target.value)}
                  className="bg-white/5 border-white/10 min-h-[70px]"
                  placeholder="Ex: mariage, rendez-vous médical, repos…"
                  data-testid="quick-leave-reason"
                />
              </Field>
            </>
          ) : (
            <>
              <Field label="Chantier *">
                <div className="relative">
                  <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                  <Input
                    value={chantierQuery}
                    onChange={(e) => { setChantierQuery(e.target.value); setChantierListOpen(true); if (form.chantier_id) set("chantier_id", null); }}
                    onFocus={() => setChantierListOpen(true)}
                    onBlur={() => setTimeout(() => setChantierListOpen(false), 150)}
                    placeholder="Nom du chantier — ou tape pour en créer un"
                    className="bg-white/5 border-white/10 pl-9"
                    data-testid="quick-iv-chantier-search"
                  />
                  {form.chantier_id && (
                    <Check size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-400" weight="bold" />
                  )}
                  {chantierListOpen && (matches.length > 0 || showCreate) && (
                    <div className="absolute z-40 left-0 right-0 mt-1 bg-neutral-950/95 backdrop-blur-md border border-white/15 rounded-xl shadow-2xl overflow-hidden max-h-64 overflow-y-auto" data-testid="quick-iv-chantier-list">
                      {matches.slice(0, 8).map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => pickChantier(c)}
                          className="w-full text-left px-3 py-2 flex items-start gap-2 border-b border-white/5 last:border-0 hover:bg-white/[0.04]"
                          data-testid={`quick-iv-ch-item-${c.id}`}
                        >
                          <div className="w-6 h-6 rounded-lg bg-[#005CFF]/20 flex items-center justify-center shrink-0 mt-0.5">
                            <HardHat size={11} weight="duotone" className="text-[#7CB2FF]" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">{c.name}</div>
                            {c.address && <div className="text-[10px] text-white/50 truncate">{c.address}</div>}
                          </div>
                        </button>
                      ))}
                      {showCreate && (
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={createChantier}
                          disabled={creatingChantier}
                          className="w-full text-left px-3 py-2.5 flex items-center gap-2 bg-emerald-500/10 hover:bg-emerald-500/20 border-t border-emerald-500/30"
                          data-testid="quick-iv-create-chantier"
                        >
                          <div className="w-6 h-6 rounded-lg bg-emerald-500/25 flex items-center justify-center shrink-0">
                            <Plus size={11} weight="bold" className="text-emerald-300" />
                          </div>
                          <div className="text-sm text-emerald-200">
                            {creatingChantier ? "Création…" : <>Créer le chantier <b>« {chantierQuery.trim()} »</b></>}
                          </div>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </Field>

              <Field label="Titre *">
                <Input data-testid="quick-iv-title" value={form.title || ""} onChange={(e) => set("title", e.target.value)} placeholder={form.type === "depannage" ? "Ex: Panne clim SAV" : "Ex: Pose tableau"} className="bg-white/5 border-white/10" />
              </Field>

              <TimeRangeInput
                scheduled_date={form.scheduled_date}
                duration_min={form.duration_min}
                onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
                testid="quick-iv-time"
              />

              <div className="rounded-xl bg-white/[0.02] border border-white/10 p-3 space-y-2">
                <div className="flex items-center gap-1.5 text-white/60 text-[10px] uppercase tracking-widest">
                  <UsersThree size={11} weight="duotone" /> Co-affectés (optionnel)
                </div>
                <AssigneesPicker
                  team={team.filter((u) => u.id !== form.technician_id)}
                  value={form.assignee_ids || []}
                  onChange={(ids) => setForm((f) => ({ ...f, assignee_ids: ids }))}
                  testid="quick-iv-assignees"
                />
              </div>

              <ColorPickerField
                value={form.color}
                onChange={(v) => setForm((f) => ({ ...f, color: v }))}
                testid="quick-iv-color"
                label="Couleur (optionnel)"
              />
            </>
          )}
        </div>

        <div className="flex gap-2 mt-4">
          <Button variant="ghost" className="flex-1 border border-white/10 hover:bg-white/5" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className={`flex-1 ${isLeave ? "bg-emerald-600 hover:bg-emerald-500" : "bg-[#005CFF] hover:bg-[#0047CC]"}`}
            data-testid="quick-iv-submit"
          >
            {saving ? "…" : isLeave ? (filingOnBehalf ? "Enregistrer le congé" : "Envoyer la demande") : (form.type === "depannage" ? "Enregistrer dépannage" : "Planifier")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-white/60 text-[11px] uppercase tracking-widest">{label}</Label>
      {children}
    </div>
  );
}
